import {
  ImmediateNotificationEvent,
  NotificationChannel,
} from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import { V1 } from '@frontend/api-ai-settings';
import { FormValues } from '@frontend/design-system';
import type { NotificationsFieldsTypes } from '../../../components/business-preferences/notifications/config';

export type NotificationsFormValues = FormValues<NotificationsFieldsTypes> & {
  patientEmergencyMessage: string;
};

type AdditionalNotificationSettings =
  V1.Types.GetAdditionalNotificationSettingsIO['output']['additionalNotificationSettings'];

type BusinessSettings = V1.Types.GetBusinessSettingsIO['output']['businessSettings'];

const channelToFormValue = (channels: NotificationChannel[] | undefined): string => {
  if (!channels) return '';
  if (
    channels.length === 2 &&
    channels.includes(NotificationChannel.EMAIL) &&
    channels.includes(NotificationChannel.SMS)
  )
    return 'both';
  else if (channels.length === 1) {
    return channels[0].toLowerCase();
  } else {
    return '';
  }
};

const formValueToChannel = (value: string): NotificationChannel[] | undefined => {
  const map: Record<string, NotificationChannel[]> = {
    email: [NotificationChannel.EMAIL],
    sms: [NotificationChannel.SMS],
    in_app: [NotificationChannel.IN_APP],
    both: [NotificationChannel.EMAIL, NotificationChannel.SMS],
  };
  return map[value];
};

/**
 * Maps each toggle key (1-9) to its corresponding ImmediateNotificationEvent enum value.
 * Order matches the NOTIFICATION_TOGGLES array in the config.
 */
const TOGGLE_EVENT_MAP: Record<`toggle${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`, ImmediateNotificationEvent> = {
  toggle1: ImmediateNotificationEvent.G_MISSING_INFO,
  toggle2: ImmediateNotificationEvent.G_BOOKING_INCOMPLETE,
  toggle3: ImmediateNotificationEvent.G_BOOKING_CONFIRMATION,
  toggle4: ImmediateNotificationEvent.G_HEADS_UP,
  toggle5: ImmediateNotificationEvent.G_BILLING_RELATED,
  toggle6: ImmediateNotificationEvent.G_LANDLINE_OR_UNRESPONSIVE,
  toggle7: ImmediateNotificationEvent.G_MISCELLANEOUS,
  toggle8: ImmediateNotificationEvent.G_UPCOMING_CUSTOMER,
  toggle9: ImmediateNotificationEvent.G_CANCELLATION_WAITLIST,
};

/**
 * Transforms API response data to form values.
 * Accepts both AdditionalNotificationSettings and BusinessSettings as sources.
 */
export const apiToFormValues = (
  notificationData: AdditionalNotificationSettings | undefined,
  businessData: BusinessSettings | undefined
): NotificationsFormValues => {
  const prefs = notificationData?.value?.preferences;
  const receiveImmediately = prefs?.receiveImmediately ?? [];
  const dentalNotifications = businessData?.value?.dentalPreferences?.notifications;

  return {
    communicationMethod: channelToFormValue(prefs?.notificationChannels),
    practiceEmail: prefs?.notificationEmails?.join(', ') ?? '',
    practicePhoneNumber: prefs?.notificationPhones?.[0] ?? '',
    // Prefer dental-specific emergency contacts; fall back to additional notification entries
    patientEmergencyEmail: dentalNotifications?.publicContactEmail ?? prefs?.notificationEmails?.slice(1) ?? [],
    patientEmergencyPhoneNumber: dentalNotifications?.publicContactPhones ?? prefs?.notificationPhones?.slice(1) ?? [],
    patientEmergencyMessage: dentalNotifications?.emergencyMessage ?? '',
    toggle1: receiveImmediately.includes(ImmediateNotificationEvent.G_MISSING_INFO),
    toggle2: receiveImmediately.includes(ImmediateNotificationEvent.G_BOOKING_INCOMPLETE),
    toggle3: receiveImmediately.includes(ImmediateNotificationEvent.G_BOOKING_CONFIRMATION),
    toggle4: receiveImmediately.includes(ImmediateNotificationEvent.G_HEADS_UP),
    toggle5: receiveImmediately.includes(ImmediateNotificationEvent.G_BILLING_RELATED),
    toggle6: receiveImmediately.includes(ImmediateNotificationEvent.G_LANDLINE_OR_UNRESPONSIVE),
    toggle7: receiveImmediately.includes(ImmediateNotificationEvent.G_MISCELLANEOUS),
    toggle8: receiveImmediately.includes(ImmediateNotificationEvent.G_UPCOMING_CUSTOMER),
    toggle9: receiveImmediately.includes(ImmediateNotificationEvent.G_CANCELLATION_WAITLIST),
    voicemailResponse: businessData?.value?.vmTimeOut ?? '',
    helpGettingIn: businessData?.value?.dentalPreferences?.callsAndMessaging?.clientOutsideDoor ?? '',
    runningLate: businessData?.value?.dentalPreferences?.callsAndMessaging?.clientRunningLate ?? '',
  };
};

/**
 * Transforms form values to the API request structure for UpdateAdditionalNotificationSettings.
 */
export const formValuesToApi = (
  formValues: Partial<NotificationsFormValues>,
  allValues?: Partial<NotificationsFormValues>
): Partial<Omit<V1.Types.UpdateAdditionalNotificationSettingsIO['input'], 'locationId' | 'orgId' | 'etag'>> => {
  const prefs: V1.Types.UpdateAdditionalNotificationSettingsIO['input']['notificationSettingsPreferences'] = {};
  // Communication channel
  if ('communicationMethod' in formValues && formValues.communicationMethod !== undefined) {
    const channel = formValueToChannel(formValues.communicationMethod);
    if (channel) {
      prefs.notificationChannels = channel;
    }
  }

  // Notification emails: primary email + emergency emails combined into one array
  const hasEmail = 'practiceEmail' in formValues;
  if (hasEmail) {
    const primary = formValues.practiceEmail ? [formValues.practiceEmail] : [];

    prefs.notificationEmails = primary;
  }

  // Notification phones: primary phone + emergency phones combined into one array
  const hasPhone = 'practicePhoneNumber' in formValues;
  if (hasPhone) {
    const primary = formValues.practicePhoneNumber ? [formValues.practicePhoneNumber] : [];
    prefs.notificationPhones = primary;
  }

  // Build receiveImmediately from toggle states
  // Uses allValues (full form state) when available, since this is a complete replacement
  // array and unchanged toggles must still be included.
  const toggleSource = allValues ?? formValues;
  const toggleKeys = Object.keys(TOGGLE_EVENT_MAP) as (keyof typeof TOGGLE_EVENT_MAP)[];
  const hasAnyToggle = toggleKeys.some((key) => key in formValues);
  if (hasAnyToggle) {
    prefs.receiveImmediately = toggleKeys
      .filter((key) => toggleSource[key] === true)
      .map((key) => TOGGLE_EVENT_MAP[key]);
  }

  return { notificationSettingsPreferences: prefs };
};

/**
 * Transforms form values to the API request structure for UpdateBusinessSettings.
 * Covers fields sourced from BusinessPreferences (voicemail, helpGettingIn, runningLate).
 */
export const businessFormValuesToApi = (
  formValues: Partial<NotificationsFormValues>
): Partial<Omit<V1.Types.UpdateBusinessSettingsIO['input'], 'locationId' | 'orgId' | 'etag'>> => {
  const businessPreferences: V1.Types.UpdateBusinessSettingsIO['input']['businessPreferences'] = {};

  if ('voicemailResponse' in formValues) {
    businessPreferences.vmTimeOut = formValues.voicemailResponse;
  }

  const hasDentalCallsFields = 'helpGettingIn' in formValues || 'runningLate' in formValues;
  if (hasDentalCallsFields) {
    businessPreferences.dentalPreferences = {
      callsAndMessaging: {
        ...('helpGettingIn' in formValues && { clientOutsideDoor: formValues.helpGettingIn }),
        ...('runningLate' in formValues && { clientRunningLate: formValues.runningLate }),
      },
    };
  }

  const hasEmergencyEmail = 'patientEmergencyEmail' in formValues;

  if (hasEmergencyEmail) {
    businessPreferences.dentalPreferences ??= {};
    businessPreferences.dentalPreferences.notifications = {
      ...businessPreferences.dentalPreferences.notifications,
      publicContactEmail: formValues.patientEmergencyEmail,
    };
  }

  const hasEmergencyPhone = 'patientEmergencyPhoneNumber' in formValues;

  if (hasEmergencyPhone) {
    businessPreferences.dentalPreferences ??= {};
    businessPreferences.dentalPreferences.notifications = {
      ...businessPreferences.dentalPreferences.notifications,
      publicContactPhones: formValues.patientEmergencyPhoneNumber,
    };
  }

  // This is not currently editable in the form, but if it were, this is how it would be handled. It is included here for completeness and future-proofing.
  // if ('patientEmergencyMessage' in formValues) {
  //   businessPreferences.dentalPreferences = {
  //     ...businessPreferences.dentalPreferences,
  //     notifications: {
  //       ...businessPreferences.dentalPreferences?.notifications,
  //       emergencyMessage: formValues.patientEmergencyMessage,
  //     },
  //   };
  // }

  return { businessPreferences };
};

export const useNotificationsData = ({
  selectedLocationIds,
  selectedOrgId,
}: {
  selectedLocationIds: string[];
  selectedOrgId: string;
}) => {
  const isMulti = selectedLocationIds.length > 1;

  const singleNotifQuery = V1.Queries.useGetAdditionalNotificationSettingsQuery({
    request: { locationId: selectedLocationIds[0], orgId: selectedOrgId },
    options: { enabled: !isMulti },
  });
  const multiNotifQueries = V1.Queries.useGetAdditionalNotificationSettingsQueries(
    selectedLocationIds.map((locationId) => ({
      request: { locationId, orgId: selectedOrgId },
      options: { enabled: isMulti },
    }))
  );

  const singleBusinessQuery = V1.Queries.useGetBusinessSettingsQuery({
    request: { locationId: selectedLocationIds[0], orgId: selectedOrgId },
    options: { enabled: !isMulti },
  });
  const multiBusinessQueries = V1.Queries.useGetBusinessSettingsQueries(
    selectedLocationIds.map((locationId) => ({
      request: { locationId, orgId: selectedOrgId },
      options: { enabled: isMulti },
    }))
  );

  const isLoading = isMulti
    ? multiNotifQueries.some((q) => q.isLoading) || multiBusinessQueries.some((q) => q.isLoading)
    : singleNotifQuery.isLoading || singleBusinessQuery.isLoading;

  return {
    isMulti,
    isLoading,
    notifDataByLocationId: isMulti
      ? Object.fromEntries(
          selectedLocationIds.map((id, idx) => [id, multiNotifQueries[idx]?.data?.additionalNotificationSettings])
        )
      : { [selectedLocationIds[0]]: singleNotifQuery.data?.additionalNotificationSettings },
    notifEtagByLocationId: isMulti
      ? Object.fromEntries(selectedLocationIds.map((id, idx) => [id, multiNotifQueries[idx]?.data?.etag]))
      : { [selectedLocationIds[0]]: singleNotifQuery.data?.etag },
    businessDataByLocationId: isMulti
      ? Object.fromEntries(
          selectedLocationIds.map((id, idx) => [id, multiBusinessQueries[idx]?.data?.businessSettings])
        )
      : { [selectedLocationIds[0]]: singleBusinessQuery.data?.businessSettings },
    businessEtagByLocationId: isMulti
      ? Object.fromEntries(selectedLocationIds.map((id, idx) => [id, multiBusinessQueries[idx]?.data?.etag]))
      : { [selectedLocationIds[0]]: singleBusinessQuery.data?.etag },
  };
};
